import React, { useRef, useState } from "react";
import { api, fileUrl } from "../lib/api";
import { useI18n } from "../lib/i18n.jsx";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Upload, Trash2 } from "lucide-react";

const SIZES = ["small", "medium", "large"];
const PX = { small: 18, medium: 24, large: 30 };

export function pdfDefaults(s) {
  const th = s.theme || {};
  return {
    logo_source: "company", pdf_logo_file_id: "", logo_size: s.brand_size || "medium",
    name_mode: s.app_wordmark_file_id ? "wordmark" : "text", name_size: s.brand_size || "medium", brand_position: "left",
    mode: "light", colors: { primary: th.primary || "#D97706", secondary: th.secondary || "#1D4ED8", heading: "", text: "", table: "" },
    bg_color: "", bg_image_file_id: "", bg_opacity: 0.15, footer_text: s.pdf_footer || "", show_page_numbers: true,
    ...(s.pdf_design || {}), colors: { primary: th.primary || "#D97706", secondary: th.secondary || "#1D4ED8", heading: "", text: "", table: "", ...((s.pdf_design || {}).colors || {}) },
  };
}

export default function PdfDesigner({ settings, onSaved }) {
  const { t } = useI18n();
  const [d, setD] = useState(() => pdfDefaults(settings));
  const logoRef = useRef(); const bgRef = useRef();
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const setC = (k, v) => setD((p) => ({ ...p, colors: { ...p.colors, [k]: v } }));
  const upload = async (file, key) => {
    const fd = new FormData(); fd.append("file", file); fd.append("entity_type", "branding");
    try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); set(key, data.id); }
    catch { toast.error(t("upload_failed")); }
  };
  const save = async () => {
    try { await api.put("/settings", { pdf_design: d }); toast.success(t("pdf_design_saved")); onSaved?.(); }
    catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };
  const seg = (k, opts, tkey = (o) => o) => (
    <div className="flex gap-1">{opts.map((o) => <button key={o} onClick={() => set(k, o)} data-testid={`pdf-${k}-${o}`} className={`flex-1 h-9 rounded-md border text-xs font-semibold ${d[k] === o ? "bg-[var(--amber)] text-white border-transparent" : "tactical-card"}`}>{t(tkey(o))}</button>)}</div>
  );
  const color = (k, label) => (
    <label className="flex items-center gap-2 tactical-card px-2 py-1.5 text-xs">
      <input type="color" value={d.colors[k] || "#000000"} onChange={(e) => setC(k, e.target.value)} className="w-7 h-7 rounded border-0 bg-transparent" data-testid={`pdf-color-${k}`} />
      <span className="flex-1">{t(label)}</span>
      {d.colors[k] && !["primary", "secondary"].includes(k) && <button onClick={() => setC(k, "")} className="text-[10px] underline">{t("reset")}</button>}
    </label>
  );
  const pdfLogo = d.pdf_logo_file_id ? fileUrl(d.pdf_logo_file_id) : null;
  const bgImg = d.bg_image_file_id ? fileUrl(d.bg_image_file_id) : null;
  const Lbl = ({ k }) => <div className="text-xs uppercase font-bold text-[var(--text-3)] mt-3 mb-1">{t(k)}</div>;

  return (
    <div className="tactical-card p-4 min-w-0" data-testid="pdf-designer">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div className="text-xs uppercase font-bold text-[var(--text-3)]">{t("pdf_design")}</div>
        <Button onClick={save} className="bg-[var(--amber)] text-white" data-testid="pdf-design-save">{t("save_pdf_design")}</Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[340px_minmax(0,1fr)] gap-4 min-w-0">
        <div className="min-w-0">
          <Lbl k="logo" />
          {seg("logo_source", ["company", "custom", "none"], (o) => `pdf_logo_${o}`)}
          {d.logo_source === "custom" && (
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <div className="w-20 h-12 tactical-card flex items-center justify-center overflow-hidden">{pdfLogo ? <img src={pdfLogo} alt="" className="max-h-10 max-w-full object-contain" data-testid="pdf-logo-preview" /> : <span className="text-[10px] text-[var(--text-3)]">—</span>}</div>
              <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], "pdf_logo_file_id")} data-testid="pdf-logo-input" />
              <Button variant="outline" size="sm" onClick={() => logoRef.current.click()} className="gap-1" data-testid="pdf-logo-upload"><Upload size={14} /> {pdfLogo ? t("replace") : t("upload")}</Button>
              {pdfLogo && <Button variant="outline" size="sm" onClick={() => set("pdf_logo_file_id", "")} className="text-[var(--red)]"><Trash2 size={14} /></Button>}
            </div>
          )}
          <div className="mt-2">{seg("logo_size", SIZES, (o) => `size_${o}`)}</div>
          <Lbl k="company_name" />
          {seg("name_mode", ["text", "wordmark", "none"], (o) => `pdf_name_${o}`)}
          <div className="mt-2">{seg("name_size", SIZES, (o) => `size_${o}`)}</div>
          <Lbl k="brand_position" />
          {seg("brand_position", ["left", "center", "right"], (o) => `pos_${o}`)}
          <Lbl k="mode" />
          {seg("mode", ["light", "dark"])}
          <Lbl k="colors" />
          <div className="grid grid-cols-1 min-[380px]:grid-cols-2 gap-2">
            {color("primary", "primary_color")}{color("secondary", "secondary_color")}{color("heading", "heading_color")}{color("text", "text_color")}{color("table", "table_color")}
          </div>
          <Lbl k="background" />
          <div className="flex items-center gap-2 flex-wrap">
            <label className="flex items-center gap-2 tactical-card px-2 py-1.5 text-xs"><input type="color" value={d.bg_color || (d.mode === "dark" ? "#0F172A" : "#FFFFFF")} onChange={(e) => set("bg_color", e.target.value)} className="w-7 h-7 rounded border-0 bg-transparent" data-testid="pdf-bg-color" /><span>{t("bg_color")}</span>{d.bg_color && <button onClick={() => set("bg_color", "")} className="text-[10px] underline">{t("reset")}</button>}</label>
            <input ref={bgRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], "bg_image_file_id")} data-testid="pdf-bg-input" />
            <Button variant="outline" size="sm" onClick={() => bgRef.current.click()} className="gap-1" data-testid="pdf-bg-upload"><Upload size={14} /> {bgImg ? t("replace") : t("bg_image")}</Button>
            {bgImg && <Button variant="outline" size="sm" onClick={() => set("bg_image_file_id", "")} className="text-[var(--red)]" data-testid="pdf-bg-remove"><Trash2 size={14} /></Button>}
          </div>
          {bgImg && <div className="mt-2 text-xs">{t("bg_opacity")}: {Math.round(d.bg_opacity * 100)}%<input type="range" min="3" max="50" value={Math.round(d.bg_opacity * 100)} onChange={(e) => set("bg_opacity", +e.target.value / 100)} className="w-full" data-testid="pdf-bg-opacity" /></div>}
          <Lbl k="footer" />
          <Input value={d.footer_text || ""} onChange={(e) => set("footer_text", e.target.value)} placeholder={t("pdf_footer")} data-testid="pdf-footer-text" />
          <label className="flex items-center gap-2 mt-2 text-sm"><input type="checkbox" checked={d.show_page_numbers !== false} onChange={(e) => set("show_page_numbers", e.target.checked)} data-testid="pdf-page-numbers" /> {t("page_numbers")}</label>
        </div>
        <div className="min-w-0 overflow-x-auto flex justify-start sm:justify-center py-2">
          <PdfPreview d={d} s={settings} />
        </div>
      </div>
    </div>
  );
}

export function PdfPreview({ d, s }) {
  const { t } = useI18n();
  const dark = d.mode === "dark";
  const bg = d.bg_color || (dark ? "#0F172A" : "#FFFFFF");
  const text = d.colors.text || (dark ? "#F1F5F9" : "#000000");
  const heading = d.colors.heading || (dark ? "#F8FAFC" : "#000000");
  const h2 = d.colors.heading || d.colors.primary;
  const table = d.colors.table || d.colors.primary;
  const stripe = dark ? "#1E293B" : "#F8FAFC";
  const grid = dark ? "#334155" : "#CBD5E1";
  const logo = d.logo_source === "none" ? null : d.logo_source === "custom" ? (d.pdf_logo_file_id ? fileUrl(d.pdf_logo_file_id) : null) : (s.logo_file_id ? fileUrl(s.logo_file_id) : s.logo_url);
  const wm = s.app_wordmark_file_id ? fileUrl(s.app_wordmark_file_id) : null;
  const name = s.app_name || s.name || "";
  const just = d.brand_position === "center" ? "center" : d.brand_position === "right" ? "flex-end" : "flex-start";
  const bgImg = d.bg_image_file_id ? fileUrl(d.bg_image_file_id) : null;
  const rows = [["1.1", t("preview_row_a"), "м²", "24", "6.0", "5.5"], ["2.3", t("preview_row_b"), "шт", "12", "3.0", "3.2"], ["4.1", t("preview_row_c"), "м", "40", "2.0", "1.8"]];
  return (
    <div data-testid="pdf-preview" className="relative shrink-0 shadow-2xl border border-slate-300 overflow-hidden" style={{ width: 320, height: 452, background: bg, color: text, fontSize: 7, fontFamily: "Inter, Arial, sans-serif" }}>
      {bgImg && <img src={bgImg} alt="" className="absolute inset-0 w-full h-full object-cover pointer-events-none" style={{ opacity: d.bg_opacity ?? 0.15 }} />}
      <div className="relative flex items-center px-3 gap-2" style={{ height: 32, background: d.colors.primary, color: "#fff", justifyContent: just }}>
        {logo && <img src={logo} alt="" className="object-contain" style={{ height: PX[d.logo_size] * 0.7, maxWidth: 70 }} />}
        {d.name_mode === "wordmark" && wm ? <img src={wm} alt="" className="object-contain" style={{ height: PX[d.name_size] * 0.7, maxWidth: 110 }} />
          : d.name_mode !== "none" && <span className="font-bold truncate" style={{ fontSize: 9 }}>{name}</span>}
        {d.brand_position !== "right" && <span className="ml-auto" style={{ fontSize: 6 }}>{t("title_worker_preview")}</span>}
      </div>
      <div className="relative px-3 pt-3 space-y-1.5">
        <div className="font-bold" style={{ fontSize: 11, color: heading }}>{t("title_worker_preview")}</div>
        <div style={{ opacity: 0.7 }}>{t("period")}: 01.06.2026 — 07.06.2026</div>
        <div className="font-bold" style={{ fontSize: 8.5, color: h2 }}>Ivan Petrov · {t("team")}: A</div>
        <div style={{ opacity: 0.7 }}>{t("project")}: Riga Tower · {t("date")}: 03.06.2026</div>
        <table className="w-full border-collapse" style={{ fontSize: 6 }}>
          <thead><tr style={{ background: table, color: "#fff" }}>{["№", t("work"), t("unit"), t("qty"), t("norm_hours"), t("actual_hours")].map((h) => <th key={h} className="text-left px-1 py-0.5 font-bold" style={{ border: `0.5px solid ${grid}` }}>{h}</th>)}</tr></thead>
          <tbody>{rows.map((r, i) => <tr key={i} style={{ background: i % 2 ? stripe : "transparent" }}>{r.map((c, j) => <td key={j} className="px-1 py-0.5" style={{ border: `0.5px solid ${grid}`, textAlign: j > 2 ? "right" : "left" }}>{c}</td>)}</tr>)}</tbody>
        </table>
        <div className="font-bold mt-1" style={{ fontSize: 8.5, color: h2 }}>{t("summary")}</div>
        <div className="flex gap-3"><span>{t("norm_hours")}: 11.0</span><span>{t("actual_hours")}: 10.5</span><span>{t("efficiency")}: 105%</span></div>
      </div>
      <div className="absolute left-3 right-3 flex justify-between" style={{ bottom: 14, fontSize: 5.5, color: "#7a8599" }}>
        <span className="truncate">{s.name} · {t("generated")}: 2026-06-07 · {t("by")}: Olha</span>
        {d.show_page_numbers !== false && <span>{t("page")} 1</span>}
      </div>
      {d.footer_text && <div className="absolute left-0 right-0 text-center truncate px-3" style={{ bottom: 4, fontSize: 5.5, color: "#7a8599" }}>{d.footer_text}</div>}
    </div>
  );
}
